import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import type { PlayerRole, RosterEntry, RosterEntryRequest } from "../../api/types";
import { computeBaseSkill } from "./rosterSkill.ts";
import { useIsMobile } from "../../hooks/useIsMobile.ts";

const ROLES: PlayerRole[] = ["PALLEGGIO", "BANDA", "CENTRALE", "OPPOSTO", "LIBERO"];

const PARAMS: { key: keyof RosterEntryRequest; label: string; hint: string }[] = [
  { key: "attack", label: "Attacco", hint: "" },
  { key: "defense", label: "Difesa", hint: "" },
  { key: "serve", label: "Battuta", hint: "" },
  { key: "wall", label: "Muro", hint: "conta solo per gli uomini non palleggiatori" },
  { key: "setting", label: "Palleggio", hint: "" },
  { key: "personality", label: "Personalità", hint: "" },
  { key: "presence", label: "Presenza", hint: "" },
];

const EMPTY: RosterEntryRequest = {
  playerId: null,
  nickname: "",
  firstName: "",
  lastName: "",
  gender: "M",
  bornDate: null,
  suitabilityCertificate: null,
  roles: [],
  shirtNumber: null,
  attack: 70,
  defense: 70,
  serve: 70,
  wall: 70,
  setting: 70,
  personality: 70,
  seniority: 1,
  presence: 80,
};

function fromEntry(e: RosterEntry): RosterEntryRequest {
  return {
    playerId: e.playerId,
    nickname: e.nickname,
    firstName: e.firstName,
    lastName: e.lastName,
    gender: e.gender,
    bornDate: e.bornDate,
    suitabilityCertificate: e.suitabilityCertificate,
    roles: e.roles,
    shirtNumber: e.shirtNumber,
    attack: e.attack,
    defense: e.defense,
    serve: e.serve,
    wall: e.wall,
    setting: e.setting,
    personality: e.personality,
    seniority: e.seniority,
    presence: e.presence,
  };
}

interface Props {
  open: boolean;
  /** null = nuovo giocatore */
  entry: RosterEntry | null;
  seasonCode: string;
  onClose: () => void;
  onSave: (data: RosterEntryRequest) => Promise<void>;
}

export default function RosterEntryForm({ open, entry, seasonCode, onClose, onSave }: Props) {
  const isMobile = useIsMobile();
  const [form, setForm] = useState<RosterEntryRequest>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(entry ? fromEntry(entry) : EMPTY);
      setError(null);
    }
  }, [open, entry]);

  const skill = useMemo(() => computeBaseSkill(form), [form]);

  const set = <K extends keyof RosterEntryRequest>(key: K, value: RosterEntryRequest[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const toggleRole = (role: PlayerRole) =>
    set("roles", form.roles.includes(role) ? form.roles.filter((r) => r !== role) : [...form.roles, role]);

  const valid = form.nickname.trim().length > 0 && form.roles.length > 0;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave({
        ...form,
        nickname: form.nickname.trim(),
        firstName: form.firstName?.trim() || null,
        lastName: form.lastName?.trim() || null,
        bornDate: form.bornDate || null,
        suitabilityCertificate: form.suitabilityCertificate || null,
      });
    } catch (err: any) {
      const msg = err?.response?.data;
      setError(typeof msg === "string" && msg ? msg : "Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ backgroundColor: "primary.main", color: "white" }}>
        {entry ? `Modifica ${entry.nickname}` : "Nuovo giocatore"} — stagione {seasonCode}
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Anagrafica</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="Nickname" required fullWidth size="small" value={form.nickname}
              onChange={(e) => set("nickname", e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField label="Nome" fullWidth size="small" value={form.firstName ?? ""}
              onChange={(e) => set("firstName", e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField label="Cognome" fullWidth size="small" value={form.lastName ?? ""}
              onChange={(e) => set("lastName", e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <ToggleButtonGroup exclusive size="small" value={form.gender} fullWidth
              onChange={(_, v) => v && set("gender", v)}>
              <ToggleButton value="M">Uomo</ToggleButton>
              <ToggleButton value="F">Donna</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField label="Data di nascita" type="date" fullWidth size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              value={form.bornDate ?? ""} onChange={(e) => set("bornDate", e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <TextField label="Scadenza certificato" type="date" fullWidth size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              value={form.suitabilityCertificate ?? ""}
              onChange={(e) => set("suitabilityCertificate", e.target.value)} />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>Ruoli *</Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
          {ROLES.map((r) => (
            <Chip key={r} label={r} clickable
              color={form.roles.includes(r) ? "primary" : "default"}
              variant={form.roles.includes(r) ? "filled" : "outlined"}
              onClick={() => toggleRole(r)} />
          ))}
        </Stack>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 3, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Parametri stagione {seasonCode}
          </Typography>
          <Chip color="secondary" label={`Skill base: ${skill}`} />
        </Box>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField label="Maglia" type="number" fullWidth size="small"
              value={form.shirtNumber ?? ""}
              onChange={(e) => set("shirtNumber", e.target.value === "" ? null : Number(e.target.value))} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField label="Anzianità (anni)" type="number" fullWidth size="small"
              slotProps={{ htmlInput: { min: 0, max: 40 } }}
              value={form.seniority}
              onChange={(e) => set("seniority", Math.max(0, Number(e.target.value)))} />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {PARAMS.map(({ key, label, hint }) => (
            <Grid key={key} size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" sx={{ display: "flex", justifyContent: "space-between" }}>
                <span>{label}{hint && <em style={{ opacity: 0.6 }}> — {hint}</em>}</span>
                <strong>{form[key] as number}</strong>
              </Typography>
              <Slider size="small" min={60} max={100} step={1}
                value={form[key] as number}
                onChange={(_, v) => set(key, v as number)} />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="contained" disabled={!valid || saving} onClick={handleSave}>
          {entry ? "Salva" : "Aggiungi alla rosa"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
